import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { FormikField } from '../../../../../../components/FormikField';
import { getInputType, getTaxTypeValue, getTaxTypes } from '../../helpers';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { paymentValidationSchema } from '../../formConfig/validations';
import {
  getPaymentFieldProps,
  PAYMENTS_LINE_ITEMS_COLUMNS,
} from './PaymentDialog.config';
import { getPaymentFormInitialValues } from '../../formConfig/initialValues';
import { v4 as uuid } from 'uuid';
import {
  calculatePaymentTotal,
  formatNumberWithCurrency,
} from '../../../../../../helpers/invoiceHelpers';
import styles from './PaymentDialog.styles.scss';
import cn from 'classnames';

const PaymentDialog = ({ items, isOpen, setIsOpen, onClose, currency }) => {
  const { t, i18n } = useTranslation();
  const [savedItems, setSavedItems] = useState(items || []);
  const [editingRows, setEditingRows] = useState([]);

  useEffect(() => {
    setSavedItems(items || []);
  }, [items, isOpen]);

  const getSubmittedItems = useCallback((values, errors) => {
    const hasTempValues = Object.values(values?.tempLineItem || {}).some(
      (value) => !!value,
    );

    if (!hasTempValues) {
      return values?.lineItems || [];
    }

    if (errors?.tempLineItem) {
      return null;
    }

    const tempItem = {
      id: values.tempLineItem.id || uuid(),
      ...values.tempLineItem,
    };

    return [...(values?.lineItems || []), tempItem];
  }, []);

  const handleAddItemClick = ({
    values,
    setFieldValue,
    isVatBreakdown = false,
  }) => {
    const clickItems = [
      ...values.lineItems,
      { id: uuid(), ...values.tempLineItem },
    ];
    setFieldValue('lineItems', clickItems);
    setSavedItems([...clickItems]);

    if (isVatBreakdown) {
      setFieldValue('tempLineItem', {
        invoiceNumber: values.tempLineItem.invoiceNumber,
        invoiceIssueDate: values.tempLineItem.invoiceIssueDate,
        taxableAmount: values.tempLineItem.taxableAmount,
        vatRate: '',
      });
    } else {
      setFieldValue(
        'tempLineItem',
        getPaymentFormInitialValues(clickItems).tempLineItem,
      );
    }
  };

  const footer = useCallback((values, errors, setFieldValue, setTouched) => (
    <Button
      text
      type="button"
      label={t('createInvoice.add_new_invoice')}
      icon="pi pi-plus-circle"
      disabled={
        Object.values(values.tempLineItem).every((value) => !value) ||
        errors?.tempLineItem
      }
      onClick={() => {
        setTouched('tempLineItem', false);
        handleAddItemClick({
          values,
          setFieldValue,
        });
      }}
    />
  ));

  const textEditor = useCallback(
    (options, col, setFieldValue, setFieldTouched) => {
      return (
        <FormikField
          name={`lineItems[${options.rowIndex - 1}][${col.key}]`}
          key={col.key + options.rowIndex}
          component={getInputType(col?.type)}
          controlProps={{
            labelProps: { className: styles.label },
          }}
          {...getPaymentFieldProps(
            i18n.language,
            t,
            (v) =>
              setFieldValue(
                `lineItems[${options.rowIndex - 1}][${col.key}]`,
                v,
              ),
            (isTouched, shouldValidate) =>
              setFieldTouched(
                `lineItems[${options.rowIndex - 1}][${col.key}]`,
                isTouched,
                shouldValidate,
              ),
          )[col.key]}
        />
      );
    },
    [i18n.language, t],
  );

  const indexColumnBody = useCallback((_data, options) => {
    return options.rowIndex + 1;
  }, []);

  const columnBody = useCallback(
    (rowData, col) => {
      if (col.body) {
        return col.body(i18n.language, rowData);
      }
      return rowData[col.key] ? rowData[col.key] : '-';
    },
    [i18n.language],
  );

  const rowEditorTemplate = useCallback(
    (rowData, options, values, errors, setFieldValue) => {
      if (rowData.isFirstRow) {
        return null;
      }

      return (
        <div key={rowData.id} className="d-flex align-items-center">
          {options?.rowEditor?.editing ? (
            <Fragment>
              <Button
                icon="pi pi-check"
                type="button"
                rounded
                text
                disabled={errors?.lineItems?.[options.rowIndex - 1]}
                onClick={(e) => {
                  options.rowEditor.onSaveClick(e);
                  const updatedItems = savedItems.map((item, index) =>
                    index === options.rowIndex - 1 ? rowData : item,
                  );
                  setSavedItems(updatedItems);
                  setEditingRows((prev) =>
                    prev.filter((id) => id !== options.dataKey),
                  );
                }}
              />
              <Button
                icon="pi pi-times"
                type="button"
                rounded
                text
                onClick={(e) => {
                  options.rowEditor.onCancelClick(e);
                  setFieldValue(
                    `lineItems[${options.rowIndex - 1}]`,
                    savedItems[options.rowIndex - 1],
                  );
                  setEditingRows((prev) =>
                    prev.filter((id) => id !== options.dataKey),
                  );
                }}
              />
            </Fragment>
          ) : (
            <Fragment>
              <Button
                icon="pi pi-file-edit"
                type="button"
                rounded
                text
                onClick={(e) => {
                  setEditingRows((prev) => [...prev, options.dataKey]);
                  options.rowEditor.onInitClick(e);
                }}
              />
              <Button
                icon="pi pi-trash"
                type="button"
                rounded
                text
                onClick={() => {
                  handleRowDelete(rowData.id, values, setFieldValue);
                  savedItems.filter((item) => item.id !== rowData.id);
                  setSavedItems((prev) =>
                    prev.filter((item) => item.id !== rowData.id),
                  );
                }}
              />
            </Fragment>
          )}
        </div>
      );
    },
    [savedItems],
  );

  const vatRateColumnBody = useCallback(
    (rowData, values) => {
      let taxableAmount, vatRate;
      if (rowData.isFirstRow) {
        ({ taxableAmount, vatRate } = values.tempLineItem);
      } else {
        ({ taxableAmount, vatRate } = rowData);
      }

      if (!taxableAmount || !vatRate) {
        return '-';
      }

      const rateEntry = getTaxTypes(i18n.language).find(
        (type) => type.value === vatRate,
      );
      const rate = rateEntry
        ? Number(rateEntry.rate)
        : getTaxTypeValue(vatRate);

      return formatNumberWithCurrency(
        (Number(taxableAmount) * (rate / 100)).toFixed(3),
        currency,
        i18n.language,
      );
    },
    [currency, i18n.language],
  );

  const handleRowDelete = (id, values, setFieldValue) => {
    const keepItems = values.lineItems.filter((item) => item.id !== id);
    setFieldValue('lineItems', keepItems);
  };

  return (
    <Formik
      validateOnMount
      enableReinitialize
      validationSchema={paymentValidationSchema({ t })}
      initialValues={getPaymentFormInitialValues(items)}
    >
      {({ values, errors, setFieldValue, setFieldTouched, resetForm }) => {
        const submittedItems = getSubmittedItems(values, errors);

        return (
          <Dialog
            className={cn('rounded-4 w-75 overflow-hidden', styles.dialog)}
            visible={isOpen}
            onHide={() => {
              setIsOpen(false);
              resetForm();
            }}
            header={
              <h3 className={styles.dialogTitle}>
                {t('createInvoice.prepayment_details')}
              </h3>
            }
            footer={
              <div className="pt-3">
                <Button
                  type="button"
                  className="px-5 color-theme-primary"
                  label={t('cancel')}
                  text
                  size="medium"
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                    setFieldValue('lineItems', items || []);
                    setSavedItems(items || []);
                  }}
                />
                <Button
                  type="button"
                  className="px-5"
                  size="medium"
                  label={t('common.done')}
                  disabled={editingRows.length > 0 || submittedItems === null}
                  onClick={() => {
                    resetForm();
                    onClose({
                      items: submittedItems,
                    });
                  }}
                />
              </div>
            }
          >
            <div className={cn(styles.paymentTable, styles.container)}>
              <h6>{t('createInvoice.info.paidAmount.title')}</h6>
              <div
                className="instructions"
                dangerouslySetInnerHTML={{
                  __html: t('createInvoice.info.paidAmount.details'),
                }}
              />
              <DataTable
                editMode="row"
                value={[
                  { ...values.lineItems?.[0], isFirstRow: true },
                  ...(values.lineItems || []),
                ]}
                footer={() =>
                  footer(values, errors, setFieldValue, setFieldTouched)
                }
                dataKey={uuid()}
              >
                <Column key="id" header="#" body={indexColumnBody} />
                {PAYMENTS_LINE_ITEMS_COLUMNS({ currency }).map((col) => (
                  <Column
                    key={col.key}
                    field={col.key}
                    header={() => (
                      <>
                        <span>{t(col.label)}</span>
                        <span className="color-red">
                          {col.required ? ' *' : ''}
                        </span>
                        {col.headerTooltip && (
                          <i
                            className="pi pi-info-circle"
                            style={{ color: 'white', marginLeft: '0.25rem' }}
                          />
                        )}
                      </>
                    )}
                    headerTooltip={
                      col.headerTooltip ? t(col.headerTooltip) : undefined
                    }
                    headerTooltipOptions={{ position: 'top' }}
                    editor={(options) =>
                      textEditor(options, col, setFieldValue, setFieldTouched)
                    }
                    body={(rowData, item) => {
                      if (item.rowIndex === 0) {
                        return (
                          <>
                            <FormikField
                              name={`tempLineItem.${col.key}`}
                              key="temp"
                              component={getInputType(col?.type)}
                              controlProps={{
                                labelProps: { className: styles.label },
                                className: !values.tempLineItem[col.key]
                                  ? styles.emptyField
                                  : '',
                              }}
                              {...getPaymentFieldProps(
                                i18n.language,
                                t,
                                (val) =>
                                  setFieldValue(`tempLineItem.${col.key}`, val),
                                (isTouched, shouldValidate) =>
                                  setFieldTouched(
                                    `tempLineItem.${col.key}`,
                                    isTouched,
                                    shouldValidate,
                                  ),
                              )[col.key]}
                            />
                            {col.key === 'taxableAmount' && (
                              <Button
                                text
                                type="button"
                                size="small"
                                label={t('createInvoice.add_another_vat')}
                                style={{ position: 'absolute' }}
                                icon="pi pi-plus-circle"
                                disabled={
                                  Object.values(values.tempLineItem).every(
                                    (value) => !value,
                                  ) || errors?.tempLineItem
                                }
                                onClick={() => {
                                  handleAddItemClick({
                                    values,
                                    setFieldValue,
                                    isVatBreakdown: true,
                                  });
                                }}
                              />
                            )}
                          </>
                        );
                      }
                      return columnBody(rowData, col);
                    }}
                  />
                ))}
                <Column
                  key="vatAmount"
                  field="vatAmount"
                  header={() => (
                    <>
                      <span>{t('createInvoice.vat_amount')}</span>
                      <span className="color-red">*</span>
                      <i
                        className="pi pi-info-circle"
                        style={{ color: 'white', marginLeft: '0.25rem' }}
                      />
                    </>
                  )}
                  headerTooltip={t('tooltips.prepaymentVatAmount')}
                  headerTooltipOptions={{ position: 'top' }}
                  body={(rowData) => vatRateColumnBody(rowData, values)}
                />
                <Column
                  rowEditor
                  header={t('common.actions')}
                  body={(rowData, options) =>
                    rowEditorTemplate(
                      rowData,
                      options,
                      values,
                      errors,
                      setFieldValue,
                    )
                  }
                />
              </DataTable>
            </div>
            <div className={styles.totalRow}>
              <p className={cn(styles.label, 'mt-4 mb-2')}>
                {t('createInvoice.total_prepaid_amount')}
              </p>
              <p className={cn(styles.value, 'py-2 px-3')}>
                {formatNumberWithCurrency(
                  calculatePaymentTotal(submittedItems || []),
                  currency,
                  i18n.language,
                )}
              </p>
            </div>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default React.memo(PaymentDialog);
